import { useEffect, useMemo, useState } from 'react';
import { Flex, Select, Text, useColorModeValue } from '@chakra-ui/react';
import { SearchBar } from '../navbar/SearchBar';
import { useTranslation } from 'react-i18next';

export interface TableFilter {
  key: string;
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  testId?: string;
}

interface TableSearchProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  searchTestId?: string;
  filters?: TableFilter[];
  debounceMs?: number;
}

export default function TableSearch({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchTestId,
  filters,
  debounceMs = 300,
}: TableSearchProps) {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState(searchValue);
  const selectBg = useColorModeValue('secondaryGray.300', 'navy.900');

  useEffect(() => {
    setLocalValue(searchValue);
  }, [searchValue]);

  const debouncedSearch = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return (value: string) => {
      clearTimeout(timer);
      timer = setTimeout(() => onSearchChange(value), debounceMs);
    };
  }, [onSearchChange, debounceMs]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
    debouncedSearch(e.target.value);
  };

  return (
    <Flex
      gap="12px"
      align="center"
      wrap="wrap"
      mb="16px"
    >
      <SearchBar
        placeholder={searchPlaceholder || t('common.search', 'Search...')}
        value={localValue}
        onChange={handleChange}
        w={{ base: '100%', md: '220px' }}
        data-testid={searchTestId}
      />
      {filters?.map((filter) => (
        <Flex key={filter.key} align="center" gap="4px">
          <Text fontSize="sm" color="secondaryGray.600" whiteSpace="nowrap">
            {filter.label}:
          </Text>
          <Select
            size="sm"
            w="150px"
            borderRadius="16px"
            bg={selectBg}
            value={filter.value}
            onChange={(e) => filter.onChange(e.target.value)}
            data-testid={filter.testId}
          >
            {filter.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Flex>
      ))}
    </Flex>
  );
}
